<template>
  <div>
    <AppPreloader v-if="!fetched" />
    <VacancyContainer
      v-else
      :id="id"
      v-slot="{ company, vacancy, isLocked, isReplyAvailable, isVacancyCompiling }"
      @ready="handleVacancyContainerReady"
    >
      <VRow justify="center">
        <VCol cols="12" md="3" lg="3" xl="2">
          <CompanySmallCard :company="company" />
        </VCol>
        <VCol cols="12" md="8">
          <RouterView />
          <VCard elevation="1" class="pa-0 pa-md-4 px-md-12 mb-5 grey--text text--darken-4">
            <div class="pt-4 pt-md-0 pl-3 pl-md-0 primary--text ml-md-n5">
              <span>&larr; </span>
              <RouterLink
                :to="{ name: 'vacancies', params: { companyId: company.id } }"
                class="body-2"
              >
                {{ $t('vacancyList') }}
              </RouterLink>
            </div>
            <VDivider class="mt-4 mt-md-3 mb-6" />
            <div class="px-3 px-md-0">
              <h1 class="display-2">
                <VTooltip bottom>
                  <template v-slot:activator="{ on }">
                    <VIcon v-show="isLocked" class="ml-md-n7 mt-md-n1" size="20" v-on="on">
                      {{ mdiLock }}
                    </VIcon>
                  </template>
                  <span>
                    {{ $t('invitationTip') }}
                  </span>
                </VTooltip>
                {{ vacancy.title }}
              </h1>
              <div class="relative">
                <span>
                  <VacancySalary :salary="vacancy.salary" />
                </span>
                <br>
                <span v-if="vacancy.address">
                  {{ vacancy.address.city }}
                </span>
                <div v-if="vacancy.status === vacancyStatus.ARCHIVE" class="archived">
                  <span class="archived__text">
                    {{ $t('vacancyArchived') }}
                  </span>
                </div>
              </div>
              <div class="d-flex flex-column flex-sm-row mt-5 mt-lg-8">
                <VBtn
                  color="primary"
                  :dark="isReplyAvailable"
                  :disabled="!isReplyAvailable || isVacancyCompiling"
                  :to="{ name: 'applyOnVacancy', params: { vacancyId: vacancy.id } }"
                  class="mr-0 mr-sm-2 mb-3 mb-sm-0"
                >
                  {{ $t('applyTitle') }}
                </VBtn>
                <VBtn
                  color="primary"
                  :dark="isReplyAvailable"
                  :disabled="!isReplyAvailable || isVacancyCompiling"
                  :to="{ name: 'vacancyReply', params: { vacancyId: vacancy.id } }"
                >
                  {{ $t('invitationTitle') }}
                </VBtn>
                <div v-show="!isReplyAvailable && vacancy.status !== vacancyStatus.ARCHIVE">
                  <div class="ml-lg-3 mt-2 mt-lg-0 body-2 d-flex dash">
                    {{ $t('invitationTip') }}
                  </div>
                </div>
              </div>
              <VacancyCard :vacancy="vacancy" class="mt-8" />
              <div class="d-flex flex-column flex-sm-row py-8">
                <VBtn
                  color="primary"
                  :dark="isReplyAvailable"
                  :disabled="!isReplyAvailable || isVacancyCompiling"
                  :to="{ name: 'applyOnVacancy', params: { vacancyId: vacancy.id } }"
                  class="mr-0 mr-sm-2 mb-3 mb-sm-0"
                >
                  {{ $t('applyTitle') }}
                </VBtn>
                <VBtn
                  color="primary"
                  :dark="isReplyAvailable"
                  :disabled="!isReplyAvailable || isVacancyCompiling"
                  :to="{ name: 'vacancyReply', params: { vacancyId: vacancy.id } }"
                >
                  {{ $t('invitationTitle') }}
                </VBtn>
                <div v-show="!isReplyAvailable && vacancy.status !== vacancyStatus.ARCHIVE">
                  <div class="ml-lg-3 mt-2 mt-lg-0 body-2 d-flex dash">
                    {{ $t('invitationTip') }}
                  </div>
                </div>
              </div>
            </div>
          </VCard>
          <VCard
            v-show="filteredVacancies.length > 0"
            elevation="1"
            class="pa-3 pa-md-7 px-md-12 mb-5"
          >
            <h3
              class="d-flex justify-space-between align-end"
              :class="$vuetify.breakpoint.mdAndUp ? 'display-2' : 'title'"
            >
              {{ $t('otherVacancies') }} "{{ company.name }}"
            </h3>
            <VDivider class="mt-2" />
            <CompanyVacanciesContainer
              :company-id="company.id"
              :exclude="vacancy.id"
              :limit="vacanciesLimit"
            />
            <div v-show="vacancies.items.length > vacanciesLimit">
              <VDivider class="mb-5" />
              <RouterLink
                :to="{ name: 'vacancies', params: { companyId: company.id } }"
                class="subtitle-2 text-decoration-none"
              >
                <template v-if="$vuetify.breakpoint.mdAndUp">
                  {{ $t('allVacancies') }} "{{ company.name }}"
                </template>
                <template v-else>
                  <!-- eslint-disable-next-line -->
                {{ $t('more') }} {{ $tc('vacancyCount', filteredVacancies.length - vacanciesLimit) }}
                </template>
              </RouterLink>
            </div>
          </VCard>
        </VCol>
      </VRow>
    </VacancyContainer>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { mdiLock } from '@mdi/js';
import useLocale from '@/compositions/useLocale';
import { ref } from '@vue/composition-api';
import AppPreloader from '@/components/AppPreloader.vue';
import { vacancyStatus } from '../enums';
import VacancyCard from '../components/Vacancy/VacancyCard.vue';
import VacancySalary from '../components/Vacancy/VacancySalary.vue';
import CompanySmallCard from '../components/Company/CompanySmallCard.vue';
import VacancyContainer from '../components/Vacancy/VacancyContainer.vue';
import CompanyVacanciesContainer from '../components/Company/CompanyVacanciesContainer.vue';
import { safeGet } from '../functions';

export default {
  name: 'Vacancy',
  components: {
    VacancyContainer,
    CompanySmallCard,
    VacancySalary,
    VacancyCard,
    CompanyVacanciesContainer,
    AppPreloader,
  },
  metaInfo() {
    return {
      title: this.pageTitle,
    };
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      vacanciesLimit: 3,
      mdiLock,
      vacancyStatus,
      fetched: false,
      token: localStorage.getItem(`vacancy_${this.id}_current_candidate`),
      step: localStorage.getItem('interviewStep'),
    };
  },
  setup() {
    const { changeLocale, getPreferredLocale, saveLocaleIfEmptyStorageValue } = useLocale();
    const pageTitle = ref('');
    return {
      changeLocale,
      getPreferredLocale,
      saveLocaleIfEmptyStorageValue,
      pageTitle,
      vacanciesLimit: ref(3),
      handleVacancyContainerReady({ vacancy }) {
        if (vacancy) {
          pageTitle.value = `${this.$t('vacancy')} ${vacancy.title}`;
          const locale = this.getPreferredLocale() ?? vacancy.script.interviewer.locale;
          changeLocale(locale);
          saveLocaleIfEmptyStorageValue(locale);
        }
      },
    };
  },
  computed: {
    ...mapState('company', ['company', 'vacancies']),
    candidate() {
      const candidate = safeGet(this.$store.state.candidate, ['data', 'candidate']);
      if (!candidate) {
        return null;
      }
      if (candidate.vacancyId !== this.id) {
        return null;
      }
      return candidate;
    },
    filteredVacancies() {
      return this.vacancies.items.filter(v => v.id !== this.id);
    },
  },
  async created() {
    if (!this.token) {
      return;
    }
    await this.$store.dispatch('candidate/fetchData', {
      vacancyId: this.id,
    });
    const { companyId } = this.$route.params;
    if (!this.token && !this.candidate) {
      return;
    }
    if (this.token && this.candidate && this.candidate.vacancyId === this.id) {
      this.$router.push({
        name: 'interview',
        params: { vacancyId: this.id, companyId, step: this.step },
      });
    }
  },
  async beforeMount() {
    const { companyId } = this.$route.params;

    await this.$store.dispatch('company/fetchVacancies', this.$route.params.companyId);
    await this.$store.dispatch('company/fetchCompany', companyId);

    if (this.company.fetchError) {
      this.fetched = true;
      throw new Error(String(this.company.fetchCode));
    }

    if (this.vacancies.fetchError) {
      this.fetched = true;
      throw new Error(String(this.company.fetchCode));
    }

    if (this?.company?.data?.companyTemplateSettings && this.$route?.query?.preview !== 'true') {
      window.location.href = `${process.env.VUE_APP_CAREER_PORTAL_URL}/${companyId}/vacancies/${this.id}`;
    } else {
      this.fetched = true;
    }
  },
};
</script>

<style lang="scss" scoped>
.archived {
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 164px;
  height: 54px;
  background: url(../assets/img/vacancy/archived.svg) no-repeat center;
  background-size: contain;
  transform: translate(-50%, -50%);

  &__text {
    color: #3b9be1;
    font-size: 14px;
    font-weight: 700;
    line-height: 100%;
    text-transform: uppercase;
    transform: rotate(-6deg);
  }
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .archived {
    transform: none;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-up')} {
  .dash {
    position: relative;
    padding-left: 30px;

    &::before {
      content: '';
      display: block;
      width: 20px;
      height: 1px;
      background: map-get($grey, 'darken-4');
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
    }
  }
}

.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
  background-color: map-get($grey, 'base') !important;
  color: #fff !important;
}
</style>

<i18n locale="en" lang="yaml">
  vacancyList: Vacancy list
  invitationTitle: Pass an online interview
  applyTitle: Apply
  invitationTip: Available by invitation only
  otherVacancies: Other jobs from
  allVacancies: All jobs from
  more: More
  vacancy: Vacancy
  vacancyCount: 0 jobs | {n} job | {n} jobs | {n} jobs
  vacancyArchived: Job is archived
</i18n>

<i18n locale="ru" lang="yaml">
  vacancyList: Список вакансий
  invitationTitle: Пройти онлайн-собеседование
  applyTitle: Откликнуться
  invitationTip: Пройти онлайн-собеседование на эту вакансию можно только по приглашению работодателя.
  otherVacancies: Другие вакансии компании
  allVacancies: Все вакансии в
  more: Ещё
  vacancy: Вакансия
  vacancyCount: 0 вакансий | {n} вакансия | {n} вакансии | {n} вакансий
  vacancyArchived: Вакансия в архиве
</i18n>
